"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FeatureAppModel = void 0;
const rxjs_1 = require("rxjs");
const Hypervisor_1 = require("./Hypervisor");
/**
 * Globally accessible information of FeatureAppModel state
 */
class FeatureAppModel {
    /**
     * Feature App Name
     */
    name;
    /**
     * Page route
     */
    route;
    /**
     * Similar to HTMLElementID, this is an optional parameter developers
     * can use to assign a custom ID to a Feature App instance.
     */
    id;
    /**
     * A reference to the Parent Feature App that instantiated this child.
     */
    parent;
    /**
     * Metadata useful for identification and search.
     */
    meta = {};
    /**
     * Tags for querying
     */
    _tags;
    /**
     * Get tags
     */
    get tags() {
        return this._tags;
    }
    /**
     * Set tags.
     * Ensure they are an array
     */
    set tags(tags) {
        this._tags = Array.isArray(tags) ? tags : [];
    }
    /**
     * Access config variables, e.g. for API access
     * Useful mostly for containers like the root doc/integrator
     */
    get config() {
        return this._config;
    }
    /**
     * Only store config variables that are defined (truthy)
     */
    set config(config) {
        if (!config)
            return;
        this._config = Object.entries(config).reduce((accumulator, [key, value]) => {
            if (!!key && !!value) {
                accumulator[key] = value;
            }
            return accumulator;
        }, {});
    }
    /**
     * Private config store
     */
    _config = {};
    /**
     * Content Data
     */
    get data() {
        return this._dataSubject?.value;
    }
    set data(data) {
        // Protect data from accidental changes
        this._dataSubject.next(Object.freeze(data));
    }
    _dataSubject;
    /**
     * Instantiate a Feature App with metadata and content data.
     * @param input Some of the readonly properties
     * @param data Initial data
     */
    constructor(input, data) {
        // Create a new observer
        this._dataSubject = new rxjs_1.BehaviorSubject(data);
        // Assign readonly values that have been provided to the constructor
        for (const key in input) {
            const objKey = key;
            this[objKey] = input[objKey];
        }
        // Make sure we use the current route
        if (!this.route && document?.location?.pathname) {
            this.route = document.location.pathname;
        }
    }
    /**
     * Subscribe to changes of Feature App data.
     * Return function for unsubscribing
     * @param callback Event callback function
     */
    subscribe(callback) {
        const subscription = this._dataSubject.subscribe(callback);
        return () => subscription.unsubscribe();
    }
    /**
     * Registry of all registered actions for this feature app
     */
    actions = new Map();
    /**
     * Register an action that other Feature app can call similar to RFC.
     * @param actionName Action name
     * @param callback Function to execute when action is dispatched
     */
    registerAction(actionName, callback) {
        if (this.actions.get(actionName)) {
            throw new Error(`Action ${actionName} already exists in Feature App ${this.name}`);
        }
        this.actions.set(actionName, callback);
        // flush the queue just in case there was a stale action ready for this listener
        setTimeout(Hypervisor_1.flushDispatchQueue, 0);
        /**
         * This function will allow us to unregister action
         */
        return () => this.actions.delete(actionName);
    }
    /**
     * Find requested action and execute its callback
     * @param actionName Action name
     * @param args List or arguments to pass to the callback
     */
    dispatch(actionName, ...args) {
        const actionFn = this.actions.get(actionName);
        if (typeof actionFn === 'function') {
            const response = actionFn(...args);
            return { success: true, response };
        }
        if (process.env.NODE_ENV === 'development') {
            console.warn(`Action ${actionName} could not be executed in Feature App ${this.name}`);
            console.log(`Action ${actionName} was fired with the following arguments:`, ...args);
        }
        // The function is not there.
        // Usually that means that the App loaded but the listener has not been assigned yet
        return { success: false, response: null };
    }
}
exports.FeatureAppModel = FeatureAppModel;
